import { createBrowserRouter, Navigate, RouterProvider, useParams } from "react-router-dom"
import { AuthScreen } from "@/components/screens/AuthScreen"
import { HomeScreen } from "@/components/screens/HomeScreen"
import { ForgotPasswordScreen } from "@/components/screens/ForgotPasswordScreen"
import { ResetPasswordScreen } from "@/components/screens/ResetPasswordScreen"
import { ActivationShell } from "@/components/screens/ActivationShell"
import { DashboardScreen } from "@/components/screens/DashboardScreen"
import { DealPipelineScreen } from "@/components/screens/DealPipelineScreen"
import { DealDetailScreen } from "@/components/screens/DealDetailScreen"
import { CapitalPipelineScreen } from "@/components/screens/CapitalPipelineScreen"
import { SettingsScreen } from "@/components/screens/SettingsScreen"
import { AccountSettingsScreen } from "@/components/screens/AccountSettingsScreen"
import { AiUsageScreen } from "@/components/screens/AiUsageScreen"
import { BillingHydrateFallback, BillingRoute } from "@/components/billing/BillingRoute"
import { SubscriptionGate } from "@/components/billing/SubscriptionGate"
import { DisclaimerGate } from "@/components/disclaimer/DisclaimerGate"
import { BeforeYouStartScreen } from "@/components/screens/BeforeYouStartScreen"
import { TargetStep } from "@/components/activation/steps/TargetStep"
import { StrategyStep } from "@/components/activation/steps/StrategyStep"
import { AreaStep } from "@/components/activation/steps/AreaStep"
import { DynamicsStep } from "@/components/activation/steps/DynamicsStep"
import { CriteriaStep } from "@/components/activation/steps/CriteriaStep"
import { hasSubscriptionAccess } from "@/lib/billing-access"
import { useSession } from "@/lib/session-context"
import { useActivation } from "@/lib/activation-context"
import { billingPlansLoader } from "@/lib/billing-plans-loader"

const CURRENT_STEP_COMPONENTS = { 1: TargetStep, 2: StrategyStep, 3: AreaStep, 4: CriteriaStep }
const LEGACY_STEP_COMPONENTS = { 1: TargetStep, 2: StrategyStep, 3: AreaStep, 4: DynamicsStep, 5: CriteriaStep }

function StepRoute() {
  const { n } = useParams()
  const { workflowVersion, isHydrating } = useActivation()
  if (isHydrating) return null
  if (Number(workflowVersion) >= 2 && Number(n) === 5) return <Navigate to="/activation/step/4" replace />

  const components = Number(workflowVersion) >= 2 ? CURRENT_STEP_COMPONENTS : LEGACY_STEP_COMPONENTS
  const Component = components[Number(n)] ?? TargetStep
  return <Component />
}

function authenticatedDestination(currentUser) {
  return hasSubscriptionAccess(currentUser) ? "/" : "/billing"
}

function AuthRoute() {
  const { currentUser } = useSession()
  return currentUser ? <Navigate to={authenticatedDestination(currentUser)} replace /> : <AuthScreen />
}

const router = createBrowserRouter([
  { path: "/sign-in", Component: AuthRoute },
  { path: "/sign-up", Component: AuthRoute },
  { path: "/confirmation-sent", Component: AuthRoute },
  { path: "/forgot-password", Component: ForgotPasswordScreen },
  { path: "/reset-password", Component: ResetPasswordScreen },
  { path: "/verify-2fa", Component: AuthRoute },
  {
    path: "/billing",
    Component: BillingRoute,
    HydrateFallback: BillingHydrateFallback,
    loader: billingPlansLoader,
  },
  {
    Component: SubscriptionGate,
    children: [
      { path: "/before-you-start", Component: BeforeYouStartScreen },
      {
        Component: DisclaimerGate,
        children: [
          { path: "/dashboard", Component: DashboardScreen },
          { path: "/deal-pipeline", Component: DealPipelineScreen },
          { path: "/deal-pipeline/:dealId", Component: DealDetailScreen },
          { path: "/capital-pipeline", Component: CapitalPipelineScreen },
          { path: "/settings", Component: SettingsScreen },
          { path: "/settings/account", Component: AccountSettingsScreen },
          { path: "/ai-usage", Component: AiUsageScreen },
          {
            path: "/activation",
            Component: ActivationShell,
            children: [
              { index: true, element: <Navigate to="step/1" replace /> },
              { path: "step/:n", Component: StepRoute },
            ],
          },
          { path: "/*", Component: HomeScreen },
        ],
      },
    ],
  },
])

export function Router() {
  return <RouterProvider router={router} />
}
